import type { CommentBody, CommentData } from '@liveblocks/client'
import { getMentionsFromCommentBody } from '@liveblocks/client'

import { MAX_NAMED_REACTORS } from './consts'
import { MentionUser } from './types'

// The stored text is only a copy; the comment renders from its Liveblocks thread.
export const hasLiveCommentCopy = (activity: { liveblocksThreadId?: string }): boolean =>
  !!activity.liveblocksThreadId

// newStatus marks abandoned legacy status-mirror threads that must never render as comments.
export const isExternalCommentThread = (thread: { metadata: Record<string, unknown> }): boolean =>
  thread.metadata.audience === 'external' && thread.metadata.newStatus === undefined

export const isInternalCommentThread = (thread: { metadata: Record<string, unknown> }): boolean =>
  thread.metadata.audience !== 'external' && thread.metadata.newStatus === undefined

// A comment renders only when it still has a body (deleted comments keep an empty tombstone).
export const visibleComments = (thread: { comments: readonly CommentData[] }): CommentData[] =>
  thread.comments.filter((comment) => comment.body)

// stringifyCommentBody (see serialize-comment-body.ts) resolves mentions to display names and is
// async; search filters synchronously during render and wants the raw mention id, so it walks here.
export const commentBodyToPlainText = (body: CommentBody | undefined): string => {
  if (!body) return ''
  const paragraphs: string[] = []
  for (const paragraph of body.content) {
    let text = ''
    for (const child of paragraph.children) {
      // Concatenated, not space-joined: bold/italic split a sentence across several text nodes
      // and the spaces already sit inside them, so joining would double them up.
      if (!('type' in child)) text += child.text
      else if (child.type === 'mention') text += child.id ?? ''
      else text += child.text ?? child.url
    }
    paragraphs.push(text)
  }
  return paragraphs.join('\n')
}

export const splitReactors = <T>(users: readonly T[]) => ({
  named: users.slice(0, MAX_NAMED_REACTORS),
  othersCount: Math.max(users.length - MAX_NAMED_REACTORS, 0),
})

export const filterMentionSuggestions = (users: MentionUser[], text: string): string[] => {
  const query = text.toLowerCase()
  return users
    .filter(
      (user) =>
        !query ||
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query),
    )
    .map((user) => user.email)
}

type UserWithEmail = { id: string; email?: string; name?: string }

export const extractMentionEmails = (body: CommentBody | undefined): string[] =>
  body ? getMentionsFromCommentBody(body).map((mention) => mention.id) : []

export const buildEmailToUserId = (users: readonly UserWithEmail[]): Map<string, string> => {
  const map = new Map<string, string>()
  for (const user of users) {
    if (user.email && user.id) {
      map.set(user.email, user.id)
    }
  }
  return map
}

export const mapEmailsToUserIds = (
  emails: readonly string[],
  emailToUserId: ReadonlyMap<string, string>,
  excludeEmail?: string,
): string[] => {
  const out: string[] = []
  const seen = new Set<string>()
  for (const email of emails) {
    if (!email || email === excludeEmail) continue
    const id = emailToUserId.get(email)
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  return out
}

export const hasMention = (body: CommentBody): boolean => extractMentionEmails(body).length > 0

// The pair the comment-added event reports; the replied event carries has_mention alone.
export const mentionEventProps = (body: CommentBody) => {
  const count = extractMentionEmails(body).length
  return { has_mention: count > 0, mention_count: count }
}
